import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CSSProperties,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { generatePath, useLocation, useNavigate } from "react-router-dom";
import { useDrop } from "react-dnd";
import { Box, Icon } from "../../../../common/components";
import { AppToolbarContext } from "../../../../context/AppToolbar";
import { Styles, palette } from "../../../../styles/theme";
import { Project } from "../../../../types";
import { ROUTES } from "../../../../constant/common.constant";
import {
  DND_ITEM_TYPES,
  DraggedDesignItem,
  DraggedProjectItem,
} from "../../../../constant/dnd.constant";
import { usePathActive } from "../../../../common/hooks/usePathActive";
import {
  getRotationStyle,
  mergeSx,
  useThemeBreakpoints,
} from "../../../../utils/themeUtils";

const styles: Styles = {
  container: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    textDecoration: "none",
  },
  accordion: {
    boxShadow: "none",
    width: "100%",
  },
  accordionSummary: {
    minHeight: "unset",
    borderRadius: "4px !important",
    "&>div": {
      margin: 0,
    },
    width: "100%",
    padding: "8px",
    fontStyle: "normal",
    fontWeight: "normal",

    color: "black",
    display: "flex",
    flexDirection: "row",
    cursor: "pointer",

    alignItems: "center",
    gap: "8px",
    "&:hover": {
      background: palette.blue.paleEffect,
    },
  },
  active: {
    background: palette.blue.pale,
    "&:hover": {
      background: palette.blue.pale,
    },
    "& .accordionExpandButton:hover": {
      background: "#D7EAF9",
    },
  },
  accordionDetails: {
    padding: "0 0 4px 8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
};

interface Props extends PropsWithChildren {
  id: string;
  name: string;
  projects: Project[];
  onProjectDrop?: (projectId: string, targetTeamId: string) => Promise<void>;
}

export const TeamNavLink = ({
  id,
  name,
  projects,
  children,
  onProjectDrop,
}: Props) => {
  const location = useLocation();

  const { checkActive } = usePathActive();
  const navigate = useNavigate();
  const { isMobile } = useThemeBreakpoints();
  const { toggleNavigationDrawerOpen } = useContext(AppToolbarContext);
  const expandTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isProjectDropping, setIsProjectDropping] = useState(false);

  const isOnProjectUnderTeam = useMemo(() => {
    const projectIds = projects.map((p) => p.id);
    return projectIds.some((id) => location.pathname.includes(id));
  }, [location.pathname, projects]);

  const [expanded, setExpanded] = useState(isOnProjectUnderTeam);
  const to = generatePath(ROUTES.TEAM, { teamId: id });

  useEffect(() => {
    if (isOnProjectUnderTeam) {
      setExpanded(true);
    }
  }, [isOnProjectUnderTeam]);

  const [{ isOver, draggedItemType }, drop] = useDrop<
    DraggedDesignItem | DraggedProjectItem,
    void,
    { isOver: boolean; draggedItemType: string | null }
  >({
    accept: [DND_ITEM_TYPES.DESIGN_CARD, DND_ITEM_TYPES.PROJECT],
    canDrop: (item) => {
      if (item.type === DND_ITEM_TYPES.PROJECT) {
        return item.teamId !== id;
      }
      return true;
    },
    drop: (item) => {
      if (item.type === DND_ITEM_TYPES.PROJECT) {
        const projectItem = item as DraggedProjectItem;
        if (onProjectDrop) {
          setIsProjectDropping(true);
          onProjectDrop(projectItem.projectId, id)
            .catch((error) => {
              console.error("❌ Error moving project:", error);
            })
            .finally(() => {
              setIsProjectDropping(false);
            });
        }
      }
    },
    collect: (monitor) => {
      const over = monitor.isOver();
      const itemType = monitor.getItemType() as string | null;
      return {
        isOver: over,
        draggedItemType: itemType,
      };
    },
  });

  useEffect(() => {
    if (
      isOver &&
      !expanded &&
      !expandTimeoutRef.current &&
      draggedItemType === DND_ITEM_TYPES.DESIGN_CARD
    ) {
      expandTimeoutRef.current = setTimeout(() => {
        setExpanded(true);
        expandTimeoutRef.current = null;
      }, 500);
    } else if (!isOver && expandTimeoutRef.current) {
      clearTimeout(expandTimeoutRef.current);
      expandTimeoutRef.current = null;
    }
  }, [isOver, expanded, draggedItemType]);

  useEffect(() => {
    return () => {
      if (expandTimeoutRef.current) {
        clearTimeout(expandTimeoutRef.current);
      }
    };
  }, []);

  const isActive = checkActive(to);
  const isProjectHover = isOver && draggedItemType === DND_ITEM_TYPES.PROJECT;

  return (
    <Box
      style={{
        ...(styles.container as CSSProperties),
        transition: "all 0.2s ease",
        ...(isProjectDropping && {
          opacity: 0.6,
        }),
      }}
      data-test={`leftNavigation.teams.team.${name}`}
    >
      <Accordion
        ref={drop as any}
        sx={mergeSx(
          styles.accordion,
          isProjectHover
            ? {
                borderRadius: "4px",
                boxShadow: `0px 0px 0px 1px ${palette.blue.secondary}`,
                mx: "1px",
              }
            : {}
        )}
        disableGutters
        expanded={expanded}
      >
        <AccordionSummary
          sx={
            {
              ...styles.accordionSummary,
              ...(isActive && styles.active),
            } as CSSProperties
          }
          onClick={() => {
            navigate(to);
            toggleNavigationDrawerOpen?.();
            setExpanded((prev) => !prev);
          }}
          expandIcon={
            <IconButton
              disableRipple
              className="accordionExpandButton"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setExpanded((prev) => !prev);
              }}
              sx={{ p: 0 }}
            >
              <Icon
                size={isMobile ? 18 : 22}
                icon="expand_more"
                style={{ ...getRotationStyle(expanded) }}
              />
            </IconButton>
          }
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Tooltip title={name} placement="right">
            <Typography
              color={palette.grey.nearBlack}
              variant="actionBoldSmallIcon"
              maxWidth={{ xs: "194px", lg: "167px" }}
              textOverflow={"ellipsis"}
              whiteSpace={"nowrap"}
              overflow={"hidden"}
            >
              {name}
            </Typography>
          </Tooltip>
        </AccordionSummary>
        <AccordionDetails sx={styles.accordionDetails}>
          {children}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
